import { noopInstrumentation } from "../instrumentation/index.js";
import { collectAsyncIterableToSet } from "../support/index.js";
import { walkDagReachableNodeIds } from "./traversal.js";
/**
 * Integrates certified-exclude membership with the observed include-side graph.
 *
 * Frontier sequencing and the decision to start another closure phase belong to the facade.
 */
export class PhaseCertifiedDifferenceState {
    includeGraph;
    certifiedExclude = new CertifiedExcludeState();
    telemetry;
    constructor(graph, telemetry) {
        this.telemetry = telemetry;
        this.includeGraph = new IncludeGraphState(graph, telemetry);
    }
    initializeInclude(startId) {
        this.includeGraph.initialize(startId);
    }
    /**
     * True when every include node observed so far has been finally yielded or excluded.
     *
     * Include graph entries represent unresolved include-side nodes: the start node is registered
     * before traversal, successors are registered before their main frontier items are scheduled,
     * certified-hit exclusions delete nodes, and yielded expanded nodes are deleted only after their
     * successors have already been registered. Pending main items for deleted nodes are therefore
     * stale and cannot change the result set.
     */
    isIncludeResolved() {
        return this.includeGraph.isEmpty();
    }
    // Difference-state policy plus include-side graph expansion. The returned result tells the
    // coordinator whether to enqueue more include work or resolve a certified hit.
    async advanceIncludeNode(nodeId) {
        const state = this.includeGraph.get(nodeId);
        if (state === undefined)
            return { kind: "ignored", reason: "stale" };
        if (this.certifiedExclude.has(nodeId)) {
            return { kind: "certified-hit" };
        }
        if (state.expanded)
            return { kind: "ignored", reason: "already-expanded" };
        const successors = await this.includeGraph.expand(nodeId);
        return { kind: "expanded", successors };
    }
    // Exclude state absorbs the closure; this method applies the resulting certified hits to the
    // include graph.
    async *applyClosureAndResolveIncludeHits(closure) {
        const { includeHits, newlyCertifiedCount } = this.certifiedExclude.absorbClosureCertification(closure, this.includeGraph);
        this.telemetry?.span.incrementCounter("certified_nodes", newlyCertifiedCount);
        yield* this.resolveIncludeHits(includeHits);
    }
    async *resolveIncludeHits(hits) {
        yield* resolveCertifiedHits(this.includeGraph, this.certifiedExclude, hits, this.telemetry);
    }
    // The facade decides when to drain; observed local graph deletion belongs to include-side state.
    *drainRemainingInclude() {
        yield* drainUncertifiedInclude(this.includeGraph, this.certifiedExclude);
    }
}
class CertifiedExcludeState {
    certified = new Set();
    has(nodeId) {
        return this.certified.has(nodeId);
    }
    absorbClosureCertification(closure, includeGraph) {
        const includeHits = new Set();
        let newlyCertifiedCount = 0;
        for (const nodeId of closure.certifiedNodes) {
            if (!this.certified.has(nodeId))
                newlyCertifiedCount++;
            this.certified.add(nodeId);
            if (includeGraph.has(nodeId))
                includeHits.add(nodeId);
        }
        return { includeHits, newlyCertifiedCount };
    }
}
async function* resolveCertifiedHits(includeGraph, certifiedExclude, hits, telemetry) {
    if (hits.size === 0)
        return;
    telemetry?.span.incrementCounter("certified_hits", hits.size);
    const classification = await classifyCertifiedHits(includeGraph, hits);
    telemetry?.span.incrementCounter("classification_runs");
    telemetry?.span.incrementCounter("classification_newer_nodes", classification.newerSideSize);
    telemetry?.span.incrementCounter("classification_older_nodes", classification.olderSideSize);
    let excludedNodes = 0;
    for (const nodeId of classification.excluded) {
        if (includeGraph.get(nodeId) !== undefined)
            excludedNodes++;
        includeGraph.detachAndRemoveNode(nodeId);
    }
    telemetry?.span.incrementCounter("classification_excluded_nodes", excludedNodes);
    for (const nodeId of classification.yieldable) {
        if (classification.excluded.has(nodeId))
            continue;
        const state = includeGraph.get(nodeId);
        if (state === undefined || !state.expanded || certifiedExclude.has(nodeId))
            continue;
        includeGraph.detachAndRemoveNode(nodeId);
        yield nodeId;
    }
}
async function classifyCertifiedHits(includeGraph, hits) {
    const newerSide = await collectAsyncIterableToSet(walkDagReachableNodeIds({
        graph: includeGraph.createPredecessorTopology(),
        instrumentation: noopInstrumentation,
    }, hits));
    const olderSide = await collectAsyncIterableToSet(walkDagReachableNodeIds({
        graph: includeGraph.createSuccessorTopology(),
        instrumentation: noopInstrumentation,
    }, hits));
    const excluded = new Set(olderSide);
    const yieldable = difference(newerSide, excluded);
    for (const hit of hits) {
        yieldable.delete(hit);
        excluded.add(hit);
    }
    return { yieldable, excluded, newerSideSize: newerSide.size, olderSideSize: olderSide.size };
}
function* drainUncertifiedInclude(includeGraph, certifiedExclude) {
    const nodeIds = includeGraph.nodeIds();
    for (const nodeId of nodeIds) {
        const state = includeGraph.get(nodeId);
        if (state === undefined || !state.expanded || certifiedExclude.has(nodeId))
            continue;
        includeGraph.detachAndRemoveNode(nodeId);
        yield nodeId;
    }
}
/**
 * Stores the include-side local DAG used for certified-hit classification and deletion.
 *
 * Include expansion owns edge discovery, so `expand()` records both successor and predecessor links
 * at the same time. Those observed links are local DAG state for certified-hit classification, not a
 * topology cache; certified-hit deletion detaches nodes from their neighbors.
 */
class IncludeGraphState {
    graph;
    telemetry;
    visited = new Map();
    constructor(graph, telemetry) {
        this.graph = graph;
        this.telemetry = telemetry;
    }
    initialize(startId) {
        this.ensureNodeState(startId);
    }
    has(nodeId) {
        return this.visited.has(nodeId);
    }
    get(nodeId) {
        return this.visited.get(nodeId);
    }
    ensureNodeState(nodeId) {
        let state = this.visited.get(nodeId);
        if (state === undefined) {
            state = {
                nodeId,
                predecessors: new Set(),
                successors: new Set(),
                expanded: false,
            };
            this.visited.set(nodeId, state);
        }
        return state;
    }
    markNodeExpanded(nodeId) {
        const state = this.ensureNodeState(nodeId);
        const expandedState = {
            ...state,
            expanded: true,
        };
        this.visited.set(nodeId, expandedState);
    }
    async expand(nodeId) {
        this.ensureNodeState(nodeId);
        this.telemetry?.span.incrementCounter("successor_expansions");
        this.telemetry?.span.incrementCounter("main_expansions");
        const successors = await this.graph.getSuccessors(nodeId);
        const successorIds = successors.map((successor) => successor.nodeId);
        this.markNodeExpanded(nodeId);
        for (const successor of successorIds) {
            this.recordExpandedEdge(nodeId, successor);
        }
        return successors;
    }
    recordExpandedEdge(nodeId, successorId) {
        const node = this.ensureNodeState(nodeId);
        const successor = this.ensureNodeState(successorId);
        successor.predecessors.add(nodeId);
        node.successors.add(successorId);
    }
    detachAndRemoveNode(nodeId) {
        const state = this.visited.get(nodeId);
        if (state === undefined)
            return;
        for (const successor of state.successors) {
            this.visited.get(successor)?.predecessors.delete(nodeId);
        }
        for (const predecessor of state.predecessors) {
            this.visited.get(predecessor)?.successors.delete(nodeId);
        }
        this.visited.delete(nodeId);
    }
    nodeIds() {
        return [...this.visited.keys()];
    }
    isEmpty() {
        return this.visited.size === 0;
    }
    createPredecessorTopology() {
        return {
            getSuccessors: async (nodeId) => [...this.getNodeOrThrow(nodeId).predecessors].map((predecessor) => ({
                nodeId: predecessor,
            })),
        };
    }
    createSuccessorTopology() {
        return {
            getSuccessors: async (nodeId) => [...this.getNodeOrThrow(nodeId).successors].map((successor) => ({ nodeId: successor })),
        };
    }
    getNodeOrThrow(nodeId) {
        const state = this.visited.get(nodeId);
        if (state === undefined)
            throw new Error("Expected include visited node.");
        return state;
    }
}
function difference(left, right) {
    const result = new Set();
    for (const item of left) {
        if (!right.has(item))
            result.add(item);
    }
    return result;
}
//# sourceMappingURL=phase-certified-difference-state.js.map